import React, { useState, useEffect, useRef, useCallback, forwardRef } from 'react';
import LoadingSpinnerImage from "./LoadingSpinnerImage.js";

const emptyFilters = () => ({
    products: [],
    productFamilies: [],
    countries: [],
    certificates: [],
    operators: []
});

const emptyEntities = () => ({
    products: [],
    productFamilies: [],
    countries: [],
    certificates: [],
    operators: []
});

/** Parent that owns shared entity data for Products + RIMS filter buttons. */
const RIMSFilters = ({ entityTypeId, legalManufacturerId, area, archive, searchCallback, currentFilters }) => {
    if (legalManufacturerId == -1)
        legalManufacturerId = null;

    const [entities, setEntities] = useState(emptyEntities());
    const [isLoadingData, setIsLoadingData] = useState(false);
    const [hasLoaded, setHasLoaded] = useState(false);
    const loadParamsRef = useRef(null);
    const inFlightRef = useRef(null);

    const getLoadKey = () => `${entityTypeId}|${legalManufacturerId}|${area}|${archive}`;

    // Invalidate cached entities when filter context changes
    useEffect(() => {
        const key = getLoadKey();
        if (loadParamsRef.current != null && loadParamsRef.current !== key) {
            setEntities(emptyEntities());
            setHasLoaded(false);
            inFlightRef.current = null;
        }
        loadParamsRef.current = key;
    }, [entityTypeId, legalManufacturerId, area, archive]);

    const ensureEntitiesLoaded = useCallback(() => {
        const key = getLoadKey();

        if (hasLoaded && loadParamsRef.current === key)
            return;

        if (inFlightRef.current === key)
            return;

        let url = "/umbraco/api/RelationshipEntity/Entities?entityType=" + entityTypeId + "&legalManufacturerId=" + legalManufacturerId;
        if (area)
            url = url + "&area=" + area;
        if (archive != null)
            url = url + "&archive=" + archive;

        inFlightRef.current = key;
        setIsLoadingData(true);

        fetch(url, {
            method: "GET",
            headers: { "Content-Type": "application/json" },
        })
            .then((response) => {
                if (!response.ok) {
                    if (response.status === 401)
                        document.dispatchEvent(new Event("api:unauthorized"));
                    throw new Error("Network response was not ok");
                }
                return response.json();
            })
            .then((filters) => {
                if (loadParamsRef.current !== key)
                    return;
                setEntities({
                    products: filters.Products || [],
                    productFamilies: filters.ProductFamilies || [],
                    countries: filters.Countries || [],
                    certificates: filters.Certificates || [],
                    operators: filters.Operators || []
                });
                setHasLoaded(true);
            })
            .catch((error) => console.log("error: " + error))
            .finally(() => {
                if (inFlightRef.current === key)
                    inFlightRef.current = null;
                if (loadParamsRef.current === key)
                    setIsLoadingData(false);
            });
    }, [entityTypeId, legalManufacturerId, area, archive, hasLoaded]);

    return (
        <div style={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
            <RIMSFilter
                mode="products"
                entities={entities}
                isLoadingData={isLoadingData}
                ensureEntitiesLoaded={ensureEntitiesLoaded}
                currentFilters={currentFilters}
                searchCallback={searchCallback}
            />
            <RIMSFilter
                mode="rims"
                entities={entities}
                isLoadingData={isLoadingData}
                ensureEntitiesLoaded={ensureEntitiesLoaded}
                currentFilters={currentFilters}
                searchCallback={searchCallback}
            />
        </div>
    );
};

const RIMSFilter = ({ mode = "rims", entities, isLoadingData, ensureEntitiesLoaded, searchCallback, currentFilters }) => {

    const isProductsMode = mode === "products";
    const buttonLabel = isProductsMode ? "Products" : "RIMS";
    const popupTitle = isProductsMode ? "Products Filters" : "RIMS Filters";

    const [popupOpened, setPopupOpened] = useState(false);
    const [totalFilterCount, setTotalFilterCount] = useState(0);
    const [textSearch, setTextSearch] = useState("");

    const [selectedProducts, setSelectedProducts] = useState([]);
    const [showProductsList, setShowProductsList] = useState(true);
    const productsRef = useRef(null);

    const [selectedProductFamilies, setSelectedProductFamilies] = useState([]);
    const [showProductFamiliesList, setShowProductFamiliesList] = useState(true);
    const productFamiliesRef = useRef(null);

    const [selectedCountries, setSelectedCountries] = useState([]);
    const [showCountriesList, setShowCountriesList] = useState(true);
    const countriesRef = useRef(null);

    const [selectedCertificates, setSelectedCertificates] = useState([]);
    const [showCertificatesList, setShowCertificatesList] = useState(true);
    const certificatesRef = useRef(null);

    const [selectedOperators, setSelectedOperators] = useState([]);
    const [showOperatorsList, setShowOperatorsList] = useState(true);
    const operatorsRef = useRef(null);

    const getModeCount = (filters) => {
        if (!filters) return 0;
        if (isProductsMode)
            return (filters.products?.length || 0) + (filters.productFamilies?.length || 0);
        return (filters.countries?.length || 0) + (filters.certificates?.length || 0) + (filters.operators?.length || 0);
    }

    useEffect(() => {
        setTotalFilterCount(getModeCount(currentFilters));
        if (isProductsMode) {
            setSelectedProducts(currentFilters?.products || []);
            setSelectedProductFamilies(currentFilters?.productFamilies || []);
        } else {
            setSelectedCountries(currentFilters?.countries || []);
            setSelectedCertificates(currentFilters?.certificates || []);
            setSelectedOperators(currentFilters?.operators || []);
        }
    }, [currentFilters, mode]);

    const expandLists = () => {
        setShowProductsList(true);
        setShowProductFamiliesList(true);
        setShowCountriesList(true);
        setShowCertificatesList(true);
        setShowOperatorsList(true);
    }

    const openPopup = () => {
        setPopupOpened(true);
        setTextSearch("");
        expandLists();
        ensureEntitiesLoaded();
    }

    const closeLists = () => {
        setShowProductsList(false);
        setShowProductFamiliesList(false);
        setShowCountriesList(false);
        setShowCertificatesList(false);
        setShowOperatorsList(false);
    }

    const closePopup = () => {
        setPopupOpened(false);
        setTextSearch("");
        closeLists();
        if (isProductsMode) {
            setSelectedProducts(currentFilters?.products || []);
            setSelectedProductFamilies(currentFilters?.productFamilies || []);
        } else {
            setSelectedCountries(currentFilters?.countries || []);
            setSelectedCertificates(currentFilters?.certificates || []);
            setSelectedOperators(currentFilters?.operators || []);
        }
    }

    const buildMergedFilters = (modeSelections) => {
        const base = currentFilters || emptyFilters();
        if (isProductsMode) {
            return {
                products: modeSelections.products,
                productFamilies: modeSelections.productFamilies,
                countries: base.countries || [],
                certificates: base.certificates || [],
                operators: base.operators || []
            };
        }
        return {
            products: base.products || [],
            productFamilies: base.productFamilies || [],
            countries: modeSelections.countries,
            certificates: modeSelections.certificates,
            operators: modeSelections.operators
        };
    }

    const applyFilters = () => {
        const modeSelections = isProductsMode
            ? { products: selectedProducts, productFamilies: selectedProductFamilies }
            : { countries: selectedCountries, certificates: selectedCertificates, operators: selectedOperators };
        const mergedFilters = buildMergedFilters(modeSelections);
        setTotalFilterCount(getModeCount(mergedFilters));
        setPopupOpened(false);
        setTextSearch("");
        closeLists();
        searchCallback(mergedFilters);
    }

    const clearFilters = () => {
        const modeSelections = isProductsMode
            ? { products: [], productFamilies: [] }
            : { countries: [], certificates: [], operators: [] };

        if (isProductsMode) {
            setSelectedProducts([]);
            setSelectedProductFamilies([]);
        } else {
            setSelectedCountries([]);
            setSelectedCertificates([]);
            setSelectedOperators([]);
        }

        const mergedFilters = buildMergedFilters(modeSelections);
        setTotalFilterCount(0);
        setTextSearch("");
        closeLists();
        setPopupOpened(false);
        searchCallback(mergedFilters);
    }

    return <>
        <div style={{ marginLeft: "15px" }}>
            <span
                className="pointer sr-menubar-button ellipsis"
                style={{ fontSize: "13px", fontWeight: "600", padding: "7px 15px 7px", cursor: "pointer" }}
                onClick={openPopup}
            >
                <i
                    className="fas fa-filter g-mr-3"
                    style={{ fontSize: "11px", color: "#585f69" }}
                ></i> {buttonLabel} {totalFilterCount > 0 ? <span> ({totalFilterCount})</span> : null}
            </span>
        </div>

        {popupOpened && (
            <div className="add-task-popup rims-filter-container">
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
                    <h3 style={{ margin: 0 }}>{popupTitle}</h3>
                    <button className="close-button" onClick={closePopup} style={{ border: "none", background: "transparent", cursor: "pointer" }}>
                        <i className="fas fa-times"></i>
                    </button>
                </div>

                {isLoadingData ? <div><LoadingSpinnerImage /></div> : <>
                    <div className="rims-filter-input-wrapper">
                        <input
                            className="rims-filter-input"
                            type="text"
                            autoFocus
                            value={textSearch}
                            onChange={(e) => setTextSearch(e.target.value)}
                            placeholder="Search..."
                        />
                        {textSearch !== "" && (
                            <div onClick={() => setTextSearch("")} className="rims-filter-clear-search">×</div>
                        )}
                    </div>

                    {isProductsMode ? <>
                        <RimsFilterBlock title={"Products"}
                            textSearch={textSearch}
                            selected={selectedProducts}
                            setSelected={setSelectedProducts}
                            setShowItemsList={setShowProductsList}
                            showItemsList={showProductsList}
                            items={entities.products}
                            ref={productsRef} />

                        <RimsFilterBlock title={"Product Families"}
                            textSearch={textSearch}
                            selected={selectedProductFamilies}
                            setSelected={setSelectedProductFamilies}
                            setShowItemsList={setShowProductFamiliesList}
                            showItemsList={showProductFamiliesList}
                            items={entities.productFamilies}
                            ref={productFamiliesRef} />
                    </> : <>
                        <RimsFilterBlock title={"Countries"}
                            textSearch={textSearch}
                            selected={selectedCountries}
                            setSelected={setSelectedCountries}
                            setShowItemsList={setShowCountriesList}
                            showItemsList={showCountriesList}
                            items={entities.countries}
                            ref={countriesRef} />

                        <RimsFilterBlock title={"Certificates"}
                            textSearch={textSearch}
                            selected={selectedCertificates}
                            setSelected={setSelectedCertificates}
                            setShowItemsList={setShowCertificatesList}
                            showItemsList={showCertificatesList}
                            items={entities.certificates}
                            ref={certificatesRef} />

                        <RimsFilterBlock title={"Operators"}
                            textSearch={textSearch}
                            selected={selectedOperators}
                            setSelected={setSelectedOperators}
                            setShowItemsList={setShowOperatorsList}
                            showItemsList={showOperatorsList}
                            items={entities.operators}
                            ref={operatorsRef} />
                    </>}

                    <div className="action-button-bar">
                        <div className="action-button-bar-left">
                            {totalFilterCount > 0 ? (
                                <button
                                    className="rims-filter-clear-btn"
                                    onClick={clearFilters}
                                >
                                    Clear filters
                                </button>
                            ) : null}
                        </div>
                        <div className="action-button-bar-center"></div>
                        <div className="action-button-bar-right">
                            <button className="action-button save-button" onClick={applyFilters}>
                                Apply filters
                            </button>
                        </div>
                    </div>
                </>}
            </div>
        )}
    </>
}

const RimsFilterBlock = forwardRef(({ title, textSearch, selected, setSelected, showItemsList, setShowItemsList, items }, ref) => {

    const hasItems = items && items.length > 0;
    const availableCount = items?.length || 0;
    const activeCount = selected?.length || 0;
    const searchLower = textSearch?.toLowerCase() || "";
    const filteredItems = hasItems
        ? items.filter(item => searchLower === "" || item.Name?.toLowerCase().includes(searchLower))
        : [];

    const addRemoveSelected = (id, add) => {
        if (add)
            setSelected([...selected.filter(item => item.Id !== id), items.find(item => item.Id === id)]);
        else
            setSelected([...selected.filter(item => item.Id !== id)]);
    }

    return (
        <div className="rims-filter-block" ref={ref}>
            <div className="rims-filter-block-header" onClick={() => setShowItemsList(!showItemsList)}>
                <label className="rims-filter-label">{title}</label>
                <span className="rims-filter-count" title={`${activeCount} active filter${activeCount === 1 ? "" : "s"} out of ${availableCount} available`}>
                    ({activeCount}/{availableCount})
                </span>
                <button className="rims-filter-plus-btn" type="button" title={showItemsList ? "Collapse" : "Expand"} aria-label={showItemsList ? "Collapse" : "Expand"}>
                    <i className={showItemsList ? "fas fa-chevron-up" : "fas fa-chevron-down"}></i>
                </button>
            </div>

            <div className="rims-filter-tags-container">
                {selected.map((item, index) => (
                    <span className="rims-filter-tag" key={index}>
                        {item.Name}
                        <button type="button" className="rims-filter-tag-remove" onClick={(e) => { e.stopPropagation(); addRemoveSelected(item.Id, false); }}>×</button>
                    </span>
                ))}
            </div>

            {showItemsList && (
                hasItems ? (
                    <div className="rims-filter-dropdown">
                        {filteredItems.map((item, index) => {
                            let isChecked = selected.find(s => s.Id === item.Id) ? true : false;
                            return (
                                <div key={index} className="rims-filter-dropdown-item">
                                    <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer" }}>
                                        <input type="checkbox" checked={isChecked} onChange={(e) => addRemoveSelected(item.Id, e.target.checked)} />
                                        {item.Name}
                                    </label>
                                </div>
                            );
                        })}
                        {filteredItems.length === 0 && (
                            <div className="rims-filter-message">No matching {title.toLowerCase()}.</div>
                        )}
                    </div>
                ) : (
                    <div className="rims-filter-message">
                        No {title} are 'Linked to' any of the items yet.
                    </div>
                )
            )}
        </div>
    );
});

export default RIMSFilters;
